import { IconBell, IconBellOff, IconClipboardCheck, IconRefresh } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { Badge, BotonExportarExcel, Button, IconAction, PageHeader, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui";
import type { BandejaAutorizacionItem } from "@/lib/types";
import type { ColumnaExcel } from "@/shared/export/exportar-excel";

const COLUMNAS_EXCEL: ColumnaExcel<BandejaAutorizacionItem>[] = [
  { header: "Folio", value: (s) => s.folio },
  { header: "Estado", value: (s) => s.estado },
  { header: "Tipo", value: (s) => s.tipo },
  { header: "Subtipo", value: (s) => s.subtipo },
  { header: "Organización", value: (s) => s.organizacionNombre },
  { header: "Área", value: (s) => s.areaNombre },
  { header: "Proveedor", value: (s) => s.proveedorRazonSocial },
  { header: "Núm. proveedor", value: (s) => s.proveedorNumero },
  { header: "RFC emisor", value: (s) => s.rfcEmisor },
  { header: "Concepto", value: (s) => s.descripcionPrimerConcepto },
  { header: "Total", value: (s) => s.totalCfdi },
  { header: "Moneda", value: (s) => s.monedaCfdi },
  { header: "Nivel actual", value: (s) => `${s.nivelActual}/${s.cantidadNiveles}` },
  { header: "Creación", value: (s) => s.fechaCreacion },
  { header: "Último evento", value: (s) => s.fechaUltimoEvento },
];

/**
 * Header de la bandeja · acciones para activar notificaciones del browser
 * (estado tri-estado: default/granted/denied) y un botón de refrescar
 * manual además del auto-refresh.
 */
type PermisoNotif = "default" | "granted" | "denied" | "unsupported";

export function BandejaHeader({
  permiso,
  onActivarNotificaciones,
  fetching,
  onRefrescar,
  obtenerFilas,
}: {
  permiso: PermisoNotif;
  onActivarNotificaciones: () => void;
  fetching: boolean;
  onRefrescar: () => void;
  /** Trae TODAS las páginas (con los filtros actuales) · alimenta el export a Excel. */
  obtenerFilas: () => Promise<BandejaAutorizacionItem[]>;
}) {
  return (
    <PageHeader
      title="Bandeja de autorización"
      description="Solicitudes pendientes en algún nivel donde tu rol puede autorizar · refresca cada 5s."
      actions={
        <>
          <BotonExportarExcel archivo="bandeja-autorizacion" hoja="Bandeja" columnas={COLUMNAS_EXCEL} obtenerFilas={obtenerFilas} />
          <Link
            to="/autorizacion/historial"
            className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border bg-background px-3.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <IconClipboardCheck size={14} />
            Mi historial
          </Link>
          {permiso === "default" ? (
            <Button variant="default" size="sm" leftSection={<IconBell size={14} />} onClick={onActivarNotificaciones}>
              Activar notificaciones
            </Button>
          ) : permiso === "granted" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge color="exito" size="md">
                  <IconBell size={12} className="mr-1" />
                  Notificaciones activas
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Recibirás notificaciones cuando llegue una nueva y la pestaña esté en segundo plano</TooltipContent>
            </Tooltip>
          ) : permiso === "denied" ? (
            <Badge color="slate" size="md">
              <IconBellOff size={12} className="mr-1" />
              Notificaciones bloqueadas
            </Badge>
          ) : null}
          <IconAction label="Refrescar" variant="default" size="lg" onClick={onRefrescar} loading={fetching} className="group">
            {/* Giro continuo y suave mientras el mouse está encima · al hacer
                click, `loading` muestra el spinner del propio botón. */}
            <IconRefresh
              size={16}
              className="transition-transform motion-safe:group-hover:animate-spin"
            />
          </IconAction>
        </>
      }
    />
  );
}
